'use client';

import { useEffect } from 'react';
import { useAuthSession } from '@/app/components/auth/useAuthSession';
import { usePathname } from 'next/navigation';

declare global {
  interface Window {
    BrevoConversationsID?: string;
    BrevoConversations?: (...args: unknown[]) => void;
    BrevoConversationsSetup?: {
      colors?: {
        buttonText?: string;
        buttonBg?: string;
        visitorBubbleBg?: string;
        agentBubbleBg?: string;
      };
      deferredLoading?: boolean;
      visitorId?: string;
    };
  }
}

const brevoId = process.env.NEXT_PUBLIC_BREVO_CONVERSATIONS_ID;
const scriptSrc = 'https://conversations-widget.brevo.com/brevo-conversations.js';

function hslToHex(hsl: string) {
  const cleaned = hsl.split('/')[0]?.trim();
  if (!cleaned) return null;
  const parts = cleaned.split(/\s+/);
  if (parts.length < 3) return null;
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1].replace('%', '')) / 100;
  const l = parseFloat(parts[2].replace('%', '')) / 100;
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) return null;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 360) + 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;

  if (hp >= 0 && hp < 1) {
    r = c; g = x;
  } else if (hp >= 1 && hp < 2) {
    r = x; g = c;
  } else if (hp >= 2 && hp < 3) {
    g = c; b = x;
  } else if (hp >= 3 && hp < 4) {
    g = x; b = c;
  } else if (hp >= 4 && hp < 5) {
    r = x; b = c;
  } else {
    r = c; b = x;
  }

  const m = l - c / 2;
  const toHex = (value: number) => Math.round((value + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export default function BrevoChatScript() {
  const { data: session, status } = useAuthSession();
  const visitorId = (session?.user as any)?.chatVisitorId as string | undefined;
  const pathname = usePathname();

  useEffect(() => {
    if (!brevoId) return;
    if (status === 'loading') return;
    const barHeightValue = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--mobile-booking-bar-height')
    );
    const useMiddle = Number.isFinite(barHeightValue) && barHeightValue > 0;
    const themeScope =
      (document.querySelector('[data-theme-scope]') as HTMLElement | null) ||
      document.body ||
      document.documentElement;
    const styles = getComputedStyle(themeScope);
    const primary = styles.getPropertyValue('--primary');
    const primaryFg = styles.getPropertyValue('--primary-foreground');
    const accent = styles.getPropertyValue('--accent');
    const ghost = styles.getPropertyValue('--ghost');
    const buttonBg = (primary && hslToHex(primary)) || '#565656';
    const buttonText = (primaryFg && hslToHex(primaryFg)) || '#f0f0f0';
    const visitorBubbleBg = (accent && hslToHex(accent)) || '#e7ffd1';
    const agentBubbleBg = (ghost && hslToHex(ghost)) || '#deffff';
    const colors = {
      buttonText,
      buttonBg,
      visitorBubbleBg,
      agentBubbleBg,
    };

    window.BrevoConversationsSetup = {
      colors,
      ...(useMiddle ? { buttonPosition: 'rm' } : {}),
      deferredLoading: false,
      ...(visitorId ? { visitorId } : {}),
    };
    window.BrevoConversationsID = brevoId;
    window.BrevoConversations =
      window.BrevoConversations ||
      function (...args: unknown[]) {
        (window.BrevoConversations as any).q = (window.BrevoConversations as any).q || [];
        (window.BrevoConversations as any).q.push(args);
      };
    window.BrevoConversations('setColors', colors);
    if (useMiddle) {
      window.BrevoConversations('setButtonPosition', 'rm');
    } else {
      window.BrevoConversations('resetButtonPosition');
    }

    let injected = false;
    let idleTimer: number | null = null;

    const injectScript = () => {
      if (injected) return;
      injected = true;
      if (document.querySelector('script[data-brevo-conversations]')) return;
      const script = document.createElement('script');
      script.async = true;
      script.src = scriptSrc;
      script.dataset.brevoConversations = 'true';
      document.head?.appendChild(script);
    };

    const scheduleInjection = () => {
      idleTimer = window.setTimeout(injectScript, 10000);
    };

    if (document.readyState === 'complete') {
      scheduleInjection();
    } else {
      const handleLoad = () => scheduleInjection();
      window.addEventListener('load', handleLoad, { once: true });
    }

    const handleInteraction = () => injectScript();
    const interactionOptions: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener('pointerdown', handleInteraction, interactionOptions);
    window.addEventListener('keydown', handleInteraction, { once: true });
    window.addEventListener('scroll', handleInteraction, interactionOptions);
    window.addEventListener('touchstart', handleInteraction, interactionOptions);
    return () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [status, visitorId, pathname]);

  useEffect(() => {
    if (!brevoId) return;
    if (status === 'loading') return;

    let attempts = 0;
    let timer: number | null = null;

    const applyOffset = () => {
      if (!window.matchMedia('(max-width: 639px)').matches) return false;
      const rootStyles = getComputedStyle(document.documentElement);
      const barHeight = parseFloat(rootStyles.getPropertyValue('--mobile-booking-bar-height')) || 88;
      const bottomValue = `calc(${barHeight}px + 16px + env(safe-area-inset-bottom, 0px))`;
      const selectors = [
        '.bwc-launcher',
        '#brevo-conversations .bwc-launcher',
        '#brevo-conversations .brevo-conversations-launcher',
        '#brevo-conversations .bwc-button',
        '.bwc-button',
        'iframe[src*="brevo"]',
        'iframe[src*="conversations"]',
        'iframe[id*="brevo"]',
        'iframe[class*="brevo"]',
        '[id*="brevo"] iframe',
      ];
      const targets = selectors.flatMap((selector) =>
        Array.from(document.querySelectorAll(selector)) as HTMLElement[]
      );
      if (targets.length === 0) return false;
      targets.forEach((target) => {
        target.style.setProperty('bottom', bottomValue, 'important');
        target.style.setProperty('right', '1rem', 'important');
        target.style.setProperty('z-index', '60', 'important');
        target.style.setProperty('visibility', 'visible', 'important');
        target.style.setProperty('opacity', '1', 'important');
      });
      return true;
    };

    const tick = () => {
      attempts += 1;
      if (applyOffset() || attempts > 20) {
        if (timer) window.clearInterval(timer);
      }
    };

    timer = window.setInterval(tick, 250);
    tick();

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [status, pathname]);

  useEffect(() => {
    if (!brevoId) return;
    if (status === 'loading') return;

    let attempts = 0;
    let timer: number | null = null;

    const ensureButtonPosition = () => {
      if (!window.matchMedia('(max-width: 639px)').matches) return false;
      const barHeightValue = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--mobile-booking-bar-height')
      );
      if (!Number.isFinite(barHeightValue) || barHeightValue <= 0) return false;
      if (!window.BrevoConversations) return false;
      window.BrevoConversations('setButtonPosition', 'rm');
      return true;
    };

    const tick = () => {
      attempts += 1;
      if (ensureButtonPosition() || attempts > 20) {
        if (timer) window.clearInterval(timer);
      }
    };

    timer = window.setInterval(tick, 250);
    tick();

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [status, pathname]);

  useEffect(() => {
    if (!brevoId) return;
    if (status === 'loading') return;
    if (!window.BrevoConversations) return;

    const displayName = session?.user?.name?.trim() || 'Anonymous Visitor';
    const email = session?.user?.email?.trim();
    const nameParts = displayName.split(/\s+/);
    const firstName = nameParts.shift() || 'Anonymous';
    const lastName = nameParts.join(' ') || null;

    window.BrevoConversations('setVisitor', email ? { name: displayName, email } : { name: displayName });
    window.BrevoConversations('updateIntegrationData', {
      email: email || null,
      firstName,
      lastName,
      phone: null,
    });
  }, [session?.user?.name, session?.user?.email, status]);

  return null;
}
